import { useState, useCallback, useImperativeHandle, type Ref, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import Language from '@wix/da-ds/pkg/Icons/24x24/Language';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import ListItemRadioButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemRadioButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { getBaseLanguage } from '@wix/da-i18next/pkg/getBaseLanguage';
import { makeSaveLanguageXHRCall } from '../simpleXHR/actionCreators';
import { addErrorNotification } from '../Notifications/redux/actionCreators';
import cn from 'classnames';
import { SUPPORTED_LANGUAGES } from '@wix/da-i18next/pkg/constants';

import s from './LanguageSelector.scss';

export interface LanguageSelectorRef {
  openModal: () => void;
}

export interface Props {
  className?: string;
  withText?: boolean;
  displayOnly?: boolean;
  ref?: Ref<LanguageSelectorRef>;
}

const LanguageSelector = ({
  className,
  withText = false,
  displayOnly = false,
  ref: refProp,
}: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const shortLang = i18n.language.split('-')[0];
  const [selectedLanguage, setSelectedLanguage] = useState(shortLang);

  const label = t('languageSelector.a11y.changeLanguage');

  const openModal = useCallback(() => {
    setSelectedLanguage(shortLang);
    setIsModalOpen(true);
  }, [shortLang]);

  useImperativeHandle(refProp as RefObject<LanguageSelectorRef>, () => ({ openModal }), [
    openModal,
  ]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleConfirm = useCallback(() => {
    dispatch(
      makeSaveLanguageXHRCall({
        language: selectedLanguage,
        onSuccess: () => {
          void i18n.changeLanguage(selectedLanguage);
          closeModal();
          window.location.reload();
        },
        onError: () => {
          dispatch(addErrorNotification(t('languageSelector.error.saveFailed')));
        },
      })
    );
  }, [dispatch, i18n, selectedLanguage, closeModal, t]);

  const currentLang = t(`languageSelector.supportedLanguage.${getBaseLanguage(i18n.language)}`);

  const modalFooter = [
    <TextButton key="cancel" size="large" variant="main" onClick={closeModal}>
      {t('languageSelector.modal.cancel')}
    </TextButton>,
    <BrandedButton key="confirm" size="large" variant="brand-green" onClick={handleConfirm}>
      {t('languageSelector.modal.confirm')}
    </BrandedButton>,
  ];

  const languageModal = (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={closeModal}
      heading={t('languageSelector.modal.title')}
      hasCloseButton
      showHeaderBorder
      showFooterBorder
      footerRight={modalFooter}
      contentClassName={s['modal-content']}
    >
      <Flex direction="column" role="radiogroup" aria-label={t('languageSelector.modal.title')}>
        {SUPPORTED_LANGUAGES.map(lang => (
          <ListItemRadioButton
            key={lang}
            name="language-preference"
            value={lang}
            label={t(`languageSelector.supportedLanguage.${lang}`)}
            checked={selectedLanguage === lang}
            onChange={() => setSelectedLanguage(lang)}
          />
        ))}
      </Flex>
    </Modal>
  );

  if (displayOnly) {
    return (
      <>
        <span className={cn(className, s['display-only'])} aria-hidden>
          <Language size={IconSize.MEDIUM} className={s['display-only-icon']} />
          {currentLang}
        </span>
        {languageModal}
      </>
    );
  }

  return (
    <>
      {withText ? (
        <TextButton
          size="large"
          variant="main"
          icon={Language}
          aria-label={label}
          className={cn(className, s['text'])}
          onClick={openModal}
        >
          {currentLang}
        </TextButton>
      ) : (
        <IconButton
          size="medium"
          icon={Language}
          aria-label={label}
          className={cn(className)}
          onClick={openModal}
        />
      )}
      {languageModal}
    </>
  );
};
LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;
